export const unallocatedQuantity = (totalStock, allocated) => {
  const stock = Number(totalStock)
  const alloc = Number(allocated)
  if (!Number.isFinite(stock) || !Number.isFinite(alloc)) return 0
  return Math.max(0, stock - alloc)
}

export const optionalNonNegativeNumber = value => {
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    typeof value === "boolean"
  ) {
    return null
  }

  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null
}

// Whether checkout would cap this segment per user, per the backend's own
// enforcement predicate (useo2o/o2o-backend#379). Deliberately NOT
// `segment.per_user_alloc`: that is the raw stored toggle, and a segment whose
// role has since moved off company_users still carries it while checkout
// applies no per-user cap at all. Keying the badge off the toggle would
// re-derive that drift as a wrong number on the client.
//
// `usage_visible` is required too, mirroring the backend's own
// `$canViewUsageData && $perUserEnforced` gate exactly: a viewer outside the
// asset's brand company receives no consumption data, so there is nothing to
// sum and the shared-pool figure is all the server computed for them.
//
// Both server checks are strict `=== true` so a backend that predates #379 —
// which simply omits `per_user_enforced` — keeps today's behaviour instead of
// throwing. That is what makes either deploy order safe.
//
// The live `per_user_alloc` toggle is consulted as well, because
// `per_user_enforced` describes the segment as the server LAST SAW IT while
// the editor mutates the toggle in place. Two paths in the editors clear it
// mid-edit: unchecking "Enable per-user allocation", and switching the segment
// to a non-internal group (which also empties `user_allocations`, so without
// this check the badge would read 0 for what just became an ordinary pool
// segment). Enabling the toggle on a segment the server did not report as
// enforced keeps the pool figure until the save round-trips, which is correct:
// nothing is enforced until it is stored.
//
// Normalises the same values the editors' own isTruthyToggle does — Drupal
// serialises booleans as strings, so a plain truthiness check would read the
// opt-out "0" as on.
const isTruthyToggle = value => value === true || value === "1" || value === 1

const segmentIsPerUserEnforced = segment =>
  segment?.usage_visible === true &&
  segment?.per_user_enforced === true &&
  isTruthyToggle(segment?.per_user_alloc)

// Sum of the grants not yet consumed. This is what a per-user segment can
// actually deliver: the pool is distributed by explicit grant, so neither an
// exhausted grant nor the unallocated remainder of the pool is available to
// anyone (useo2o/o2o-backend#377).
//
// Returns null — not 0 — when any row is unreadable, so the caller can fall
// back to authoritative server data rather than silently under-reporting.
const sumRemainingGrants = segment => {
  const rows = segment?.user_allocations
  if (!Array.isArray(rows)) return null

  let total = 0
  for (const row of rows) {
    // A uid can hold a grant in two company_users segments on one asset, and
    // checkout honours only the winning one. The backend stamps that verdict
    // per row as `enforced` and counts only the winners
    // (MyStockSingleResource::sumWinningGrantRemainder). Skipping the losers
    // here keeps this recompute equal to the aloc_available it overrides;
    // without it the badge re-advertises units nobody can order through this
    // segment, which is exactly what the server side stopped doing.
    // A row without an explicit verdict is not safe to count once it carries
    // a positive grant: the user may lose the cross-segment checkout winner.
    // Zero-valued padding rows are harmless and keep the live editor usable.
    if (row?.enforced === false) continue

    const granted = optionalNonNegativeNumber(row?.aloc)
    const ordered = optionalNonNegativeNumber(row?.ordered)
    if (granted === null || ordered === null) return null
    if (row?.enforced !== true && granted !== 0) return null
    total += Math.max(0, granted - ordered)
  }
  return total
}

// The ceiling a segment's availability is clamped to, before the stock cap.
// Shared by segmentAvailableNow and updateSegmentConfiguredAllocation so the
// displayed badge and the cached `aloc_available` can never drift apart.
//
// Returns null when the per-user ceiling cannot be recomputed locally, which
// tells the caller to trust the server's already-clamped `aloc_available`
// instead of recomputing. Falling back to the configured pool here would
// restore the exact bug #379 removes.
const segmentAvailabilityCeiling = (segment, configured, available) => {
  if (!segmentIsPerUserEnforced(segment)) {
    return configured ?? available
  }

  const remaining = sumRemainingGrants(segment)
  if (remaining !== null) return remaining

  // The aggregate remaining total includes losing-segment grants, so it is
  // not a safe fallback for this segment. Keep the server's clamped figure.
  return null
}

// Units the grantees of a per-user segment have consumed, capped per grantee
// at their own grant: SUM over grants of min(granted, ordered). Resolved from
// the first usable source, because only a capped figure is safe to subtract.
//
// 1. `aloc_consumed_total`, a pre-capped key the backend may add later. Reading
//    it now keeps the frontend forward compatible.
// 2. `aloc_granted_total - aloc_remaining_total`. The backend computes
//    remaining_total as SUM of max(0, granted - ordered) per grantee, so the
//    difference is exactly SUM of min(granted, ordered).
//
// `aloc_ordered_total` is deliberately not a source. It is a plain sum that is
// not capped by each grant, so a grantee who ordered more than their own grant
// would free units that another grantee can still order.
//
// Returns null when neither source is readable (or granted is below remaining,
// which cannot happen for real data), so the caller can keep the configured
// pool rather than guess.
const segmentConsumedUnits = segment => {
  const consumed = optionalNonNegativeNumber(segment?.aloc_consumed_total)
  if (consumed !== null) return consumed

  const granted = optionalNonNegativeNumber(segment?.aloc_granted_total)
  const remaining = optionalNonNegativeNumber(segment?.aloc_remaining_total)
  if (granted !== null && remaining !== null && granted >= remaining) {
    return granted - remaining
  }

  return null
}

// Units a segment still holds out of the unallocated bucket
// (useo2o/o2o-backend#414).
//
// An order lowers a shared pool, so its configured size already is what is
// left in it. A per-user pool is not lowered: its consumption is read from
// order history, while the warehouse stock falls with every shipment. Counting
// the configured size against stock then subtracts every ordered unit twice,
// once through the lower stock and once through the untouched pool (SKU
// 152993: stock 154, pool 246 fully ordered, Unallocated read 0, and no unit
// could be handed to anyone else).
//
// The agreed rule (recorded with the client on #414) is that a per-user pool
// counts only what its people can still order:
//
//   held = min(stock, max(0, pool - SUM over grants of min(granted, ordered)))
//
// This function returns the pool part, max(0, pool - consumed), with consumed
// resolved by segmentConsumedUnits: `aloc_consumed_total` first, then
// `aloc_granted_total - aloc_remaining_total`. The stock cap is applied by the
// caller through unallocatedQuantity.
//
// `aloc_ordered_total` alone is not used. It is uncapped, so a grantee who
// ordered more than their own grant would free units that another grantee can
// still order (pool 100, grants 50 and 50, first grantee ordered 80: the
// capped consumption is 50, the uncapped one is 80). /api/my-listing sends the
// capped `aloc_consumed_total`. A backend that sends only the uncapped key
// leaves the configured pool, which is the safe figure.
//
// Every other case also returns the configured pool, which is exactly what the
// screens summed before: a shared pool, a viewer without usage rights, a
// backend that does not send the usage fields yet, a digital asset (uncapped
// stock, so its pool keeps its meaning).
//
// Deliberately not capped by stock here. Capping each segment by stock and
// capping only the final Unallocated subtraction give the same Unallocated for
// non-negative pools, and leaving it uncapped keeps the Internal and Retail
// figures reading the way they always have.
export const segmentHeldUnits = (segment, { digital = false } = {}) => {
  const configured = parseInt(segment?.aloc)
  if (digital || !segmentIsPerUserEnforced(segment)) return configured

  const consumed = segmentConsumedUnits(segment)
  if (consumed === null) return configured

  return Math.max(0, configured - consumed)
}

// Sum of segmentHeldUnits over a list of segments. Unreadable pools count as
// zero here, so one malformed row cannot turn a whole comparison into NaN.
export const heldUnitsTotal = (segments, options) =>
  Array.isArray(segments)
    ? segments.reduce(
        (sum, segment) => sum + (segmentHeldUnits(segment, options) || 0),
        0
      )
    : 0

export const segmentAvailableNow = (segment, totalStock) => {
  const available = optionalNonNegativeNumber(segment?.aloc_available)
  if (available === null) return null

  // Recompute from the segment's current configured `aloc` (the same formula
  // updateSegmentConfiguredAllocation applies on an `aloc` edit) so raising
  // total stock alone also lifts the badge, instead of only ever capping the
  // stale cached `aloc_available` downward. Fall back to the cached value
  // when stock is unknown or `aloc` is unparseable.
  const stock = optionalNonNegativeNumber(totalStock)
  if (stock === null) return available

  const configured = optionalNonNegativeNumber(segment?.aloc)
  const ceiling = segmentAvailabilityCeiling(segment, configured, available)
  // Ceiling unresolvable for an enforced segment — the server already clamped
  // `aloc_available` correctly, so use it as-is rather than recomputing.
  if (ceiling === null) return available

  return Math.max(0, Math.min(stock, ceiling))
}

export const updateSegmentConfiguredAllocation = (
  segment,
  configuredAloc,
  totalStock
) => {
  const next = { ...segment, aloc: configuredAloc }
  const cached = optionalNonNegativeNumber(segment?.aloc_available)
  if (cached === null) return next

  const configured = optionalNonNegativeNumber(configuredAloc) ?? 0
  // Issue #379: editing the pool cannot raise what a per-user segment can
  // deliver — that is bounded by the grants, not by the pool — so this must
  // use the same ceiling the badge does or the cached value and the rendered
  // value disagree the moment a brand touches Configured qty.
  const ceiling = segmentAvailabilityCeiling(next, configured, cached)
  if (ceiling === null) return next

  const stock = optionalNonNegativeNumber(totalStock)
  next.aloc_available =
    stock === null
      ? Math.max(0, ceiling)
      : Math.max(0, Math.min(stock, ceiling))

  return next
}

export const userUsageForDisplay = (allocation, totalStock) => {
  const ordered = optionalNonNegativeNumber(allocation?.ordered)
  const serverRemaining = optionalNonNegativeNumber(allocation?.remaining)
  const configured = optionalNonNegativeNumber(allocation?.aloc)

  if (ordered === null || serverRemaining === null || configured === null) {
    return null
  }

  const remaining = Math.max(0, configured - ordered)
  const stock = optionalNonNegativeNumber(totalStock)
  const serverAvailable = optionalNonNegativeNumber(allocation?.aloc_available)
  const availableNow =
    stock !== null
      ? Math.min(stock, remaining)
      : serverAvailable !== null
      ? Math.min(serverAvailable, remaining)
      : remaining

  return {
    ordered,
    remaining,
    availableNow,
    exhausted: configured > 0 && remaining === 0,
    enforced: allocation?.enforced,
  }
}

const writableUserAllocation = allocation => ({
  uid: allocation?.uid,
  aloc: allocation?.aloc,
})

const writableSegment = segment => ({
  roles: segment?.roles,
  aloc: segment?.aloc,
  type: segment?.type,
  restriction_number: segment?.restriction_number,
  restriction_frequency: segment?.restriction_frequency,
  per_user_alloc: segment?.per_user_alloc,
  // "Count orders from" date collected by the digital editor. The backend
  // stock API does not yet return or persist it — today the value is
  // discarded server-side, and a stock PATCH even wipes a previously stored
  // field_aloc_effective_date because segments are recreated without it.
  // Kept in the writable set so the pending backend fix
  // (useo2o/o2o-backend#318) becomes effective without a frontend change.
  effective_date: segment?.effective_date,
  user_allocations: Array.isArray(segment?.user_allocations)
    ? segment.user_allocations.map(writableUserAllocation)
    : [],
})

export const stockPatchBody = body => ({
  ...body,
  segments: Array.isArray(body?.segments)
    ? body.segments.map(writableSegment)
    : body?.segments,
})
